import type { Scenario } from '@finsim/model';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Bands, McMetric } from '../../../entities/simulation';
import type { TableSpec } from './chart-card';
import type { CanvasData } from './use-canvas';

export interface Fan {
  scenario: Scenario;
  bands: Bands;
}

export function useFans(data: CanvasData, metric: McMetric, scenarios: readonly Scenario[]): Fan[] {
  const { mc, valueMode } = data;
  return useMemo(
    () =>
      scenarios.flatMap((scenario) => {
        const bands = mc?.scenarios[scenario.id]?.bands[metric][valueMode];
        return bands ? [{ scenario, bands }] : [];
      }),
    [mc, metric, valueMode, scenarios],
  );
}

/** "View as table" for a fan chart: P10 / median / P90 at each year end, per variant. */
export function useFanTable(
  data: CanvasData,
  metric: McMetric,
  scenarios: readonly Scenario[],
  caption: string,
): TableSpec {
  const { t } = useTranslation();
  const fans = useFans(data, metric, scenarios);
  const many = fans.length > 1;
  return {
    caption,
    columns: [
      t('charts.year'),
      ...fans.flatMap(({ scenario }) =>
        ['P10', t('mc.median'), 'P90'].map((c) => (many ? `${scenario.name} ${c}` : c)),
      ),
    ],
    rows: data.buckets.map((b) => ({
      label: String(b.year),
      values: fans.flatMap(({ bands }) =>
        [bands.p10, bands.p50, bands.p90].map((v) => data.fmt.money(v[b.to] ?? 0)),
      ),
    })),
  };
}
